"use client";

import React, { useState } from "react";
import { PhoneMissed, CircleCheck, Shield, ArrowRight, Smartphone, Clock, User, CalendarCheck, CircleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Link } from "@/components/common/Link";
export default function CaptureEveryOpportunity() {
  const [isShieldActive, setIsShieldActive] = useState(false);
  return <section id="capture-every-opportunity" className="relative w-full py-24 overflow-hidden bg-foreground text-white">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/20 rounded-full blur-[120px] opacity-40" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-blue-900/20 rounded-full blur-[100px] opacity-30" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {}
          <div className="flex flex-col space-y-8">
            <div className="inline-flex items-center space-x-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 w-fit backdrop-blur-sm">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-gray-300 tracking-wide">
                Meet <span className="text-white font-bold">Shield</span> Agent
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
              Never Miss a <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">
                Business Call Again
              </span>
            </h2>

            <p className="text-lg md:text-xl text-gray-400 max-w-xl leading-relaxed">
              62% of calls to small businesses go unanswered. With <span className="text-primary font-semibold">Shield</span>, 
              our overflow protection agent, every missed call becomes a captured lead. 
              It answers instantly, qualifies prospects, and books appointments 24/7.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-white px-8 h-14 text-lg rounded-xl shadow-[0_0_20px_rgba(37,99,235,0.3)]">
                <Link to="/contact-us">
                  Deploy Shield Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-white/20 bg-transparent text-white hover:bg-white/10 h-14 text-lg rounded-xl">
                <Link to="/solutions">
                  Explore Solutions
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-6 pt-8 border-t border-white/10">
              <div>
                <h4 className="text-3xl font-bold text-white">100%</h4>
                <p className="text-sm text-gray-400 mt-1">Call Answer Rate</p>
              </div>
              <div>
                <h4 className="text-3xl font-bold text-white">24/7</h4>
                <p className="text-sm text-gray-400 mt-1">Instant Availability</p>
              </div>
            </div>
          </div>

          {}
          <div className="relative">
            {}
            <div className="relative bg-gradient-to-b from-gray-900 to-black border border-white/10 rounded-3xl shadow-2xl overflow-hidden backdrop-blur-xl">
              
              {}
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 bg-white/5">
                <div className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${isShieldActive ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]'}`} />
                  <span className="text-sm font-medium text-gray-300">
                    {isShieldActive ? 'Shield AI Active' : 'Standard Voicemail'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                    {isShieldActive ? 'On' : 'Off'}
                  </span>
                  <Switch checked={isShieldActive} onCheckedChange={setIsShieldActive} className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-700" />
                </div>
              </div>

              {}
              <div className="p-8 min-h-[400px] flex flex-col justify-center relative transition-all duration-500">
                
                {}
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none"></div>
                
                {}
                <div className="relative z-10 w-full max-w-sm mx-auto">
                  
                  {!isShieldActive ? <div className="animate-in fade-in zoom-in-95 duration-300 space-y-6">
                      <div className="flex flex-col items-center text-center space-y-4">
                        <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center border border-red-500/20 shadow-[0_0_30px_rgba(239,68,68,0.15)]">
                          <PhoneMissed className="w-10 h-10 text-red-500" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-white">Missed Call</h3>
                          <p className="text-red-400 font-medium mt-1">Potential Lead Lost</p>
                        </div>
                      </div>

                      <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 space-y-3">
                        <div className="flex items-start gap-3">
                          <CircleAlert className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                          <div className="space-y-1">
                            <p className="text-sm text-gray-300">
                              Caller hung up without leaving a voicemail.
                            </p>
                            <p className="text-xs text-gray-500">2 minutes ago • +1 (555) 123-4567</p>
                          </div>
                        </div>
                      </div>

                      <div className="text-center">
                        <p className="text-sm text-gray-500">
                          Switch on Shield to capture this opportunity automatically.
                        </p>
                      </div>
                    </div> : <div className="animate-in fade-in zoom-in-95 duration-300 space-y-6">
                      <div className="flex flex-col items-center text-center space-y-4">
                        <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center border border-green-500/20 shadow-[0_0_30px_rgba(34,197,94,0.15)]">
                          <CircleCheck className="w-10 h-10 text-green-500" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-white">Call Handled</h3>
                          <p className="text-green-400 font-medium mt-1">Lead Qualified & Booked</p>
                        </div>
                      </div>

                      <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 space-y-4">
                        {}
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                            <Smartphone className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">Incoming Call Answered</p>
                            <p className="text-xs text-gray-500">Instantly by Shield Agent</p>
                          </div>
                        </div>
                        
                        {}
                        <div className="w-0.5 h-4 bg-primary/20 ml-4"></div>

                        {}
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                            <User className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">Lead Qualified</p>
                            <p className="text-xs text-gray-500">Interest: HVAC Repair</p>
                          </div>
                        </div>

                        {}
                        <div className="w-0.5 h-4 bg-primary/20 ml-4"></div>

                        {}
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                            <CalendarCheck className="w-4 h-4 text-green-500" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">Appointment Booked</p>
                            <p className="text-xs text-green-400">Tomorrow, 10:00 AM</p>
                          </div>
                        </div>
                      </div>
                    </div>}
                </div>
              </div>

              {}
              <div className="px-6 py-4 bg-white/5 border-t border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-xs text-gray-500">Response Time</span>
                </div>
                <span className={`text-sm font-mono font-medium ${isShieldActive ? 'text-green-400' : 'text-red-400'}`}>
                  {isShieldActive ? '< 1s' : 'N/A'}
                </span>
              </div>
            </div>

            {}
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-primary/5 rounded-full blur-3xl opacity-20"></div>
          </div>

        </div>
      </div>
    </section>;
}